import { motion } from "framer-motion";
import { Check } from "lucide-react";

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1727893119356-1702fe921cf9?crop=entropy&cs=srgb&fm=jpg&q=85";
const DEFAULT_TITLE = "Obsessed with the details you never see";
const DEFAULT_TEXT = "Werkhaus — German for \"work house\" — is exactly that. A workshop built around one belief: German machines deserve German discipline. No shortcuts, no guesswork, no surprises on the invoice.";
const DEFAULT_POINTS = [
  "Factory-level diagnostic training on Porsche, BMW, Mercedes-AMG & Audi",
  "Dealer-level diagnostic equipment — PIWIS, ISTA, XENTRY, ODIS",
  "OEM and motorsport-grade parts only, every fastener torqued to spec",
  "Transparent fixed quotes before any work begins",
];
const DEFAULT_STATS = [
  { value: "2+", label: "Years of Experience" },
  { value: "24h", label: "Diagnostics Turnaround" },
];

export default function About({ content }) {
  const image = content?.about_image || DEFAULT_IMAGE;
  const title = content?.about_title || DEFAULT_TITLE;
  const text = content?.about_text || DEFAULT_TEXT;
  const points = content?.about_points?.length ? content.about_points : DEFAULT_POINTS;
  const stats = content?.stats?.length ? content.stats : DEFAULT_STATS;

  const titleWords = title.split(" ");
  const splitAt = Math.max(1, Math.ceil(titleWords.length / 2));
  const titleA = titleWords.slice(0, splitAt).join(" ");
  const titleB = titleWords.slice(splitAt).join(" ");

  return (
    <section id="about" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="about-section">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -top-4 -left-4 w-full h-full border border-crimson/40 pointer-events-none" />
          <img
            src={image}
            alt="Inside the Werkhaus workshop"
            className="relative w-full h-[420px] sm:h-[520px] object-cover"
            loading="lazy"
          />
          <div className="absolute bottom-5 left-5 bg-[#0A0D12]/85 backdrop-blur px-4 py-3 border border-[#263342]">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-crimson">BAY 03 /// ENGINE BUILD</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-crimson font-semibold mb-4">{content?.about_eyebrow || "03 /// THE WORKSHOP"}</p>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-100 leading-[0.95]">
            {titleA} <span className="text-outline">{titleB}</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">{text}</p>
          <ul className="mt-8 space-y-4">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-slate-300">
                <span className="mt-0.5 shrink-0 w-5 h-5 bg-crimson/15 border border-crimson/50 flex items-center justify-center">
                  <Check size={12} className="text-crimson" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      <div
        className="mt-20 grid grid-cols-2 gap-4 lg:grid-cols-[repeat(var(--stat-cols),minmax(0,1fr))]"
        style={{ "--stat-cols": Math.min(stats.length, 4) }}
        data-testid="about-stats"
      >
        {stats.map((s, i) => (
          <motion.div
            key={`${s.label}-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="bg-panel border border-[#263342] p-6 sm:p-8"
          >
            <p className="font-display font-extrabold text-4xl sm:text-5xl text-crimson">{s.value}</p>
            <p className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
